/**
 * Parlays API Client
 * Shared between web and mobile for parlay tracking and management
 */

import type { BetStatus } from '../betResult';
import type { BetContext } from '../contracts/domainSchemas';
import type {
  GetParlaysResponseOutput,
  TrackedLegResponse,
  TrackedParlayResponse,
} from '../contracts/responseContracts';
import type { BoxScoreGame } from '../gameLegs';
import type { LeagueKey } from '../league';
import type { LegProvenanceFields } from '../legProvenance';
import type { HttpClient } from './httpClient';

// =============================================================================
// Types
// =============================================================================

export type { BetContext } from '../contracts/domainSchemas';

export interface TrackedLeg {
  id: number;
  player: string;
  playerId?: number | null;
  team?: string | null;
  opponentTeam?: string | null;
  /** True when `team` plays at home. */
  isHomeGame?: boolean | null;
  prop: string;
  statType: string;
  marketKey?: string | null;
  line: number | null;
  overUnder: string | null; // "Over" | "Under" | null
  odds: string | null;
  hit?: boolean | null;
  dnp?: boolean | null;
  voidReason?: string | null;
  actualValue: number | null;
  settled: boolean;
  settledAt?: string | null;
  gameId?: string | null;
  gameDate?: string | null;
  gameStartsAt?: string | null;
  origin?: string | null;
  isTracked?: boolean;
  isAIImproved?: boolean;
  isBetVerified?: boolean;
  betContext?: BetContext | null;
  leagueKey: LeagueKey;
}

export interface TrackedParlay {
  id: number;
  origins: string[];
  totalOdds: number | null;
  finalOdds: number | null;
  stake: number | null;
  result: BetStatus;
  payout: number | null;
  settledAt: string | null;
  createdAt: string;
  sportsbook?: string | null;
  confidence?: string | null;
  reasoning?: string | null;
  name?: string | null;
  tier?: string | null;
  isSgp?: boolean;
  isTracked: boolean;
  isAIImproved: boolean;
  isBetVerified: boolean;
  league: LeagueKey;
  trackedStrategyScore?: number | null;
  trackedStrategyRec?: string | null;
  trackedPredictionScore?: number | null;
  trackedPredictionRec?: string | null;
  /** False when a leg's league was untrusted when the parlay was tracked; null before the field. */
  trackedLabelTrusted?: boolean | null;
  legs: TrackedLeg[];
}

export interface NewParlayLeg extends LegProvenanceFields {
  player: string;
  playerId?: number | null;
  team?: string | null;
  opponentTeam?: string | null;
  prop?: string;
  statType?: string;
  line?: number | null;
  overUnder?: string | null;
  odds?: string | null;
  gameId?: string | null;
  gameDate?: string;
  gameStartsAt?: string;
  marketKey?: string | null;
  aiEdge?: string | null;
  isB2b?: boolean | null;
  blowoutRisk?: string | null;
  isHomeGame?: boolean | null;
  analysisConfig?: unknown | null;
  leagueKey: LeagueKey;
}

export interface TrackParlayInput {
  legs: NewParlayLeg[];
  name?: string;
  stake?: number;
  sportsbook?: string;
  tier?: string;
  totalOdds?: string;
  confidence?: string;
  reasoning?: string;
  origins?: Array<'ai' | 'manual' | 'bet'>;
  isAIImproved?: boolean;
  selectedGameIds?: string[];
  generationId?: string;
  aiAnalysisVerdict?: 'good' | 'okay' | 'risky' | 'bad';
  // Scores locked at track time (for "Then vs Now" comparison)
  trackedStrategyScore?: number;
  trackedPredictionScore?: number;
  trackedStrategyRec?: string;
  trackedPredictionRec?: string;
  league: LeagueKey;
}

/** The most parlays GET /api/parlays returns in one page; a larger `limit` is cut to it. */
export const MAX_PARLAYS_PAGE_SIZE = 200;

export interface GetParlaysParams {
  limit?: number;
  page?: number;
  results?: BetStatus[];
  origins?: ('ai' | 'manual' | 'bet')[];
  sportsbooks?: string[];
  /** Only the parlays with a leg on this game (`legsOnGame`). */
  game?: BoxScoreGame;
}

export interface ParlayStats {
  total: number;
  won: number;
  lost: number;
  pending: number;
  voided: number;
  aiCount: number;
  manualCount: number;
  betCount: number;
}

export interface GetParlaysResponse {
  parlays: TrackedParlay[];
  stats: ParlayStats;
  total: number;
  totalPages: number;
  hasMore: boolean;
}

// =============================================================================
// Server response → shared type mapping
// =============================================================================

function mapServerLeg(raw: TrackedLegResponse): TrackedLeg {
  return {
    id: raw.id,
    player: raw.player ?? 'Unknown',
    playerId: raw.playerId ?? null,
    team: raw.team ?? null,
    opponentTeam: raw.opponentTeam ?? null,
    isHomeGame: raw.isHomeGame ?? null,
    prop: raw.prop ?? '',
    statType: raw.statType ?? '',
    marketKey: raw.marketKey ?? null,
    line: raw.line != null ? Number(raw.line) : null,
    overUnder: raw.overUnder ?? null,
    odds: raw.odds ?? null,
    hit: raw.hit ?? null,
    dnp: raw.dnp ?? null,
    voidReason: raw.voidReason ?? null,
    actualValue: raw.actualValue != null ? Number(raw.actualValue) : null,
    settled: raw.settledAt != null,
    settledAt: raw.settledAt ?? null,
    gameId: raw.gameId ?? null,
    gameDate: raw.gameDate ?? null,
    gameStartsAt: raw.gameStartsAt ?? null,
    origin: raw.origin ?? null,
    isTracked: raw.isTracked ?? false,
    isAIImproved: raw.isAIImproved ?? false,
    isBetVerified: raw.isBetVerified ?? false,
    betContext: raw.betContext ?? null,
    leagueKey: raw.leagueKey,
  };
}

function mapServerParlay(raw: TrackedParlayResponse): TrackedParlay {
  return {
    id: raw.id,
    origins: raw.origins ?? [],
    totalOdds: raw.totalOdds != null ? Number(raw.totalOdds) : null,
    finalOdds: raw.finalOdds != null ? Number(raw.finalOdds) : null,
    stake: raw.stake != null ? Number(raw.stake) : null,
    result: raw.result ?? 'pending',
    payout: raw.payout != null ? Number(raw.payout) : null,
    settledAt: raw.settledAt ?? null,
    createdAt: raw.createdAt ?? new Date().toISOString(),
    sportsbook: raw.sportsbook ?? null,
    confidence: raw.confidence ?? null,
    reasoning: raw.reasoning ?? null,
    name: raw.name ?? null,
    tier: raw.tier ?? null,
    isSgp: raw.isSgp ?? false,
    isTracked: true,
    isAIImproved: raw.isAIImproved ?? false,
    isBetVerified: raw.isBetVerified ?? false,
    trackedStrategyScore:
      raw.trackedStrategyScore != null ? Number(raw.trackedStrategyScore) : null,
    trackedStrategyRec: raw.trackedStrategyRec ?? null,
    trackedPredictionScore:
      raw.trackedPredictionScore != null ? Number(raw.trackedPredictionScore) : null,
    trackedPredictionRec: raw.trackedPredictionRec ?? null,
    trackedLabelTrusted: raw.trackedLabelTrusted ?? null,
    league: raw.league,
    legs: (raw.legs ?? []).map(mapServerLeg),
  };
}

// =============================================================================
// API Client Factory
// =============================================================================

export function createParlaysApi(client: HttpClient) {
  return {
    /**
     * Get tracked parlays with optional filters and pagination
     */
    async getParlays(params?: GetParlaysParams): Promise<GetParlaysResponse> {
      const queryParams: Record<string, string | number> = {};

      if (params?.limit) queryParams.limit = params.limit;
      if (params?.page) queryParams.page = params.page;
      if (params?.results) queryParams.results = params.results.join(',');
      if (params?.origins) queryParams.origins = params.origins.join(',');
      if (params?.sportsbooks) queryParams.sportsbooks = params.sportsbooks.join(',');
      if (params?.game) {
        const { gameId, leagueKey, homeTeam, awayTeam, gameDay } = params.game;
        queryParams.gameId = gameId;
        queryParams.leagueKey = leagueKey;
        if (homeTeam) queryParams.homeTeam = homeTeam;
        if (awayTeam) queryParams.awayTeam = awayTeam;
        if (gameDay) queryParams.gameDay = gameDay;
      }

      const data = await client.get<GetParlaysResponseOutput>('/api/parlays', queryParams);
      const { stats, pagination } = data;

      // The contract says these three are always present, and the handler builds all of
      // them. Reading through `?.` anyway keeps a malformed response mapping to zeroes
      // instead of throwing, which is what the client did before it was typed.
      return {
        parlays: (data.parlays ?? []).map(mapServerParlay),
        stats: {
          total: stats?.total ?? 0,
          won: stats?.won ?? 0,
          lost: stats?.lost ?? 0,
          pending: stats?.pending ?? 0,
          voided: stats?.voided ?? 0,
          aiCount: stats?.aiCount ?? 0,
          manualCount: stats?.manualCount ?? 0,
          betCount: stats?.betCount ?? 0,
        },
        total: pagination?.total ?? 0,
        totalPages: pagination?.totalPages ?? 1,
        hasMore: pagination?.hasMore ?? false,
      };
    },

    /**
     * Track a new parlay
     */
    async trackParlay(input: TrackParlayInput): Promise<TrackedParlay> {
      const data = await client.post<{ parlay: TrackedParlayResponse }>(
        '/api/parlays/track',
        input,
      );
      return mapServerParlay(data.parlay);
    },

    /**
     * Get details for a specific parlay
     */
    async getParlayDetails(parlayId: number): Promise<TrackedParlay> {
      const data = await client.get<{ parlay: TrackedParlayResponse }>(`/api/parlays/${parlayId}`);
      return mapServerParlay(data.parlay);
    },

    /**
     * Delete a parlay
     */
    async deleteParlay(parlayId: number): Promise<void> {
      await client.deleteNoContent(`/api/parlays/${parlayId}`);
    },
  };
}

export type ParlaysApi = ReturnType<typeof createParlaysApi>;
